import React, { useEffect, useMemo, useRef, useState, lazy } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationOnIcon,
  PhotoCamera as PhotoCameraIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  InsertDriveFile as InsertDriveFileIcon,
  MusicNote as MusicNoteIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  alertAtom,
  leadDetailObjAtom,
  messageListAtom,
  userDetailAtom,
} from "../../Services/_states";
import { InterveneActions } from "../../Services/_actions/Intervene/intervene.actions";
import { LeadFormActions } from "../../Services/_actions";
import { dateFormate } from "../../utils/helper";
import MessageNode from "./MessageNode";
import DataNotFound from "../Shared/DataNotFound";
import { useScreenshot } from "use-react-screenshot";

const ModalForms = lazy(() => import("../../components/Shared/Modals"));

/**
 * ChatBox — MUI popup style (uses theme.palette.primary.main)
 * -----------------------------------------------------------
 * - Preserves your previous logic (pagination on scroll, send text/media, screenshot upload, fullscreen, refresh).
 * - Keeps MessageNode rendering.
 */
const ChatBox: React.FC = () => {
  const theme = useTheme();
  const actions = InterveneActions();
  const leadFormActions = LeadFormActions();

  // recoil state
  const userDetails = useRecoilValue(userDetailAtom);
  const messageList = useRecoilValue(messageListAtom);
  const [isLoading, setIsLoading] = useRecoilState(alertAtom);
  const leadDetails = useRecoilValue(leadDetailObjAtom);

  // ui state
  const [isOpen, setIsOpen] = useState(true); // show panel inline; toggle if you want a launcher
  const [message, setMessage] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [screenMode, setScreenMode] = useState(false);

  // uploads
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [docType, setDocType] = useState<string>("");

  // menus & refs
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const uploadDocumentsRef = useRef<HTMLInputElement>(null);
  const uploadAudioRef = useRef<HTMLInputElement>(null);
  const uploadVideoRef = useRef<HTMLInputElement>(null);

  // screenshot
  const [screenCapture, takeScreenshot] = useScreenshot();
  const [captureModal, setCaptureModal] = useState(false);

  // derived
  const topTitle = useMemo(() => {
    if (!userDetails || Object.keys(userDetails).length === 0) return "Chat Assistant";
    return userDetails?.whatsapp_name
      ? `${userDetails.whatsapp_name} (${userDetails.phone_number})`
      : userDetails?.phone_number || "Chat Assistant";
  }, [userDetails]);

  // actions
  const getMessagesByNumber = async (filter: any) => {
    setIsLoading(true);
    if (filter?.phone_number) await actions.getUserMessages(filter);
  };

  const scrollToBottom = () => {
    if (!chatContainerRef.current) return;
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  };

  const handleScroll = () => {
    if (
      chatContainerRef.current &&
      messageList?.messages?.length < (messageList?.total_count || 0)
    ) {
      if (chatContainerRef.current.scrollTop === 0) {
        const next = pageNumber + 1;
        setPageNumber(next);
        getMessagesByNumber({ ...userDetails, page: next });
      }
    }
  };

  const enterFullscreen = () => {
    const el = panelRef.current as any;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
  };
  const exitFullscreen = () => {
    const d: any = document;
    if (d.exitFullscreen) d.exitFullscreen();
    else if (d.webkitExitFullscreen) d.webkitExitFullscreen();
    else if (d.mozCancelFullScreen) d.mozCancelFullScreen();
  };
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      setScreenMode(true);
      enterFullscreen();
    } else {
      setScreenMode(false);
      exitFullscreen();
    }
  };

  const handleCapture = async () => {
    if (!panelRef.current) return;
    const shot = await takeScreenshot(panelRef.current);
    if (shot) setCaptureModal(true);
  };

  const handleSaveScreenshot = async () => {
    if (!screenCapture) return;
    try {
      const res = await fetch(screenCapture);
      const blob = await res.blob();
      const form = new FormData();
      const ts = new Date().getTime();
      form.append("file", blob, `screenshot_${ts}.png`);
      await leadFormActions.uploadLeadImageById(leadDetails?.id, form);
    } catch (e) {
      console.error("Screenshot upload failed:", e);
    } finally {
      setCaptureModal(false);
    }
  };

  const openAttachMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeAttachMenu = () => setAnchorEl(null);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const accept = e.currentTarget.accept || "";
    setDocType(accept);
    const files = e.currentTarget.files ? Array.from(e.currentTarget.files) : [];
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const deleteSelected = (idx: number) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setDocType("");
      return next;
    });
  };

  const sendMessageByNumber = async () => {
    const phone = (userDetails as any)?.phone_number;
    if (!phone) return;

    const form = new FormData();
    form.append("phone_number", phone);

    switch (docType) {
      case "image/*":
        form.append("file", selectedFiles[0]);
        form.append("text", message);
        form.append("type", "image");
        break;
      case ".doc, .docx, .pdf, .txt":
        form.append("file", selectedFiles[0]);
        form.append("text", message);
        form.append("type", "file");
        break;
      case "audio/*":
        form.append("file", selectedFiles[0]);
        form.append("type", "audio");
        break;
      case "video/*":
        form.append("file", selectedFiles[0]);
        form.append("text", message);
        form.append("type", "video");
        break;
      default:
        form.append("text", message);
        form.append("type", "text");
        break;
    }

    await actions.sendMessageV1(form);
    setMessage("");
    setSelectedFiles([]);
    setDocType("");
  };

  // effects
  useEffect(() => {
    if (userDetails && Object.keys(userDetails).length) {
      setPageNumber(1);
      getMessagesByNumber({ ...userDetails, page: 1 });
    }
  }, [userDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [messageList]);

  // styles that use primary.main
  const headerBg = theme.palette.primary.main;
  const accent = theme.palette.primary.main;

  if (!isOpen) return null;

  const hasUser = userDetails && Object.keys(userDetails).length !== 0;

  return (
    <>
      <Box
        ref={panelRef}
        sx={{
          position: "relative",
          width: 380,
          height: 400,
          bgcolor: "#F5F5F5",
          borderRadius: 3,
          boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #E0E0E0",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: headerBg,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.25)" }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {topTitle}
              </Typography>
              {hasUser && (userDetails as any)?.updated_at && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Last seen {dateFormate((userDetails as any).updated_at, "DD/MM/YYYY hh:mm A")}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => getMessagesByNumber({ ...userDetails, page: 1 })}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open map">
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() =>
                  window.open(
                    "https://www.google.com/maps/place/0%C2%B000'00.0%22N+0%C2%B000'00.0%22E/@0,0,17z/data=!3m1!4b1?entry=ttu",
                    "_blank",
                    "width=1200,height=800"
                  )
                }
              >
                <LocationOnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Screenshot chat">
              <IconButton size="small" sx={{ color: "#fff" }} onClick={handleCapture}>
                <PhotoCameraIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={screenMode ? "Exit fullscreen" : "Enter fullscreen"}>
              <IconButton size="small" sx={{ color: "#fff" }} onClick={toggleFullscreen}>
                {screenMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" sx={{ color: "#fff" }} onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Messages area */}
        <Box
          ref={chatContainerRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            p: 1.5,
            bgcolor: "#ECE5DD",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!hasUser ? (
            <DataNotFound message="No Data Found !" />
          ) : (
            messageList?.messages?.map((msg: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  mb: 1.2,
                  display: "flex",
                  justifyContent: msg?.origin === "INBOUND" ? "flex-start" : "flex-end",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "75%",
                    p: 1.25,
                    borderRadius: 2,
                    // bgcolor: msg?.origin === "INBOUND" ? "#fff" : "#DCF8C6",
                    border: "none",
                    wordBreak: "break-word",
                  }}
                >
                  <MessageNode message={msg} />
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Selected uploads preview (chips) */}
        {!!selectedFiles.length && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: "#fff",
              borderTop: "1px solid #EEE",
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {selectedFiles.map((f, i) => (
              <Chip key={i} label={f.name} onDelete={() => deleteSelected(i)} />
            ))}
          </Box>
        )}

        {/* Composer */}
        <Box
          sx={{
            p: 1.25,
            bgcolor: "#fff",
            borderTop: "1px solid #DDD",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            onClick={openAttachMenu}
            sx={{
              bgcolor: "#ECE5DD",
              color: accent,
              width: 40,
              height: 40,
              "&:hover": { bgcolor: "#e3dccf" },
            }}
          >
            <AddIcon />
          </IconButton>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeAttachMenu}>
            <MenuItem
              onClick={() => {
                closeAttachMenu();
                uploadImageRef.current?.click();
              }}
            >
              <ImageIcon sx={{ mr: 1 }} /> Image
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeAttachMenu();
                uploadDocumentsRef.current?.click();
              }}
            >
              <DescriptionIcon sx={{ mr: 1 }} /> Documents
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeAttachMenu();
                uploadAudioRef.current?.click();
              }}
            >
              <MusicNoteIcon sx={{ mr: 1 }} /> Audio
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeAttachMenu();
                uploadVideoRef.current?.click();
              }}
            >
              <InsertDriveFileIcon sx={{ mr: 1 }} /> Video
            </MenuItem>
          </Menu>

          {/* hidden inputs */}
          <input
            ref={uploadImageRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFileChange}
          />
          <input
            ref={uploadDocumentsRef}
            type="file"
            accept=".doc, .docx, .pdf, .txt"
            multiple
            hidden
            onChange={handleFileChange}
          />
          <input ref={uploadAudioRef} type="file" accept="audio/*" hidden onChange={handleFileChange} />
          <input ref={uploadVideoRef} type="file" accept="video/*" hidden onChange={handleFileChange} />

          <TextField
            variant="outlined"
            size="small"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (message.trim().length || selectedFiles.length)) {
                sendMessageByNumber();
              }
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#F0F0F0",
                borderRadius: "20px",
              },
              "& input": { px: 1.5, py: 1.25, fontSize: 14 },
            }}
          />

          {(message.trim().length || selectedFiles.length) && (
            <Button
              onClick={sendMessageByNumber}
              variant="contained"
              sx={{
                bgcolor: accent,
                color: "#fff",
                width: 40,
                height: 40,
                minWidth: 40,
                borderRadius: "50%",
                "&:hover": { bgcolor: theme.palette.primary.dark },
              }}
            >
              <SendIcon fontSize="small" />
            </Button>
          )}
        </Box>
      </Box>

      {/* Screenshot preview modal */}
      {screenCapture && (
        <ModalForms
          show={captureModal}
          handleClose={() => {
            setCaptureModal(false);
          }}
          modalBody={
            <Box sx={{ textAlign: "center" }}>
              <img src={screenCapture} alt="Screenshot of chat" style={{ maxWidth: "100%" }} />
              <Divider sx={{ my: 2 }} />
              <Button onClick={handleSaveScreenshot} variant="contained" sx={{ bgcolor: accent }}>
                Upload
              </Button>
            </Box>
          }
          modalHeader={"Screenshot"}
          modalId={"Screenshot"}
          modalclassName={""}
          size="m"
        />
      )}
    </>
  );
};

export default ChatBox;
